import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, LogOut, GraduationCap, KeyRound, Plus, ExternalLink } from "lucide-react";

export default function ParentDashboard() {
  const [me, setMe] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkCode, setLinkCode] = useState("");
  const [linking, setLinking] = useState(false);
  const nav = useNavigate();

  const token = localStorage.getItem("brimi_parent_token");
  const client = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  const load = async () => {
    try {
      const [m, c] = await Promise.all([client.get("/parent/me"), client.get("/parent/children")]);
      setMe(m.data);
      setChildren(c.data);
    } catch (e) {
      localStorage.removeItem("brimi_parent_token");
      nav("/parent/login");
    }
  };

  useEffect(() => { if (!token) { nav("/parent/login"); return; } load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, []);

  const linkChild = async () => {
    if (!linkCode) return;
    setLinking(true);
    try {
      await client.post("/parent/link", { invite_code: linkCode });
      toast.success("Child linked!");
      setLinkCode("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLinking(false); }
  };

  const logout = () => { localStorage.removeItem("brimi_parent_token"); nav("/"); };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0B1120] text-white"><Loader2 className="w-6 h-6 animate-spin text-yellow-400" /></div>;

  return (
    <div className="min-h-screen bg-[#0B1120] grid-noise text-white pb-20">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrimiLogo size={36} withBadge showText textColor="white" subtitle={me?.name || "Parent"} />
          <button onClick={logout} className="text-sm font-bold text-slate-300 hover:text-white flex items-center gap-2"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 pt-10" data-testid="parent-dashboard">
        <h1 className="font-display text-4xl md:text-5xl">Your children&apos;s progress</h1>
        <p className="text-slate-400 mt-2">Track each child&apos;s roadmap in one view.</p>

        <div className="mt-8 brimi-card p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold flex items-center gap-2"><KeyRound className="w-4 h-4" /> Link another child</p>
          <div className="mt-3 flex gap-2">
            <input data-testid="parent-link-code" value={linkCode} onChange={(e) => setLinkCode(e.target.value)} placeholder="BRIMI-XXXXXX" className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-slate-900 outline-none uppercase" />
            <button data-testid="parent-link-btn" onClick={linkChild} disabled={linking || !linkCode} className="brimi-pill accent-coral disabled:opacity-60">
              {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 inline mr-1" /> Link</>}
            </button>
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {children.length === 0 && <p className="text-slate-400">No children linked yet.</p>}
          {children.map((c) => (
            <div key={c.user_id} className="brimi-card p-6" data-testid={`child-${c.user_id}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-slate-900 font-display text-xl" style={{ background: "#FBBF24" }}>
                  {c.user?.name?.[0]?.toUpperCase() || "S"}
                </div>
                <div>
                  <p className="font-display text-xl text-slate-900">{c.user?.name}</p>
                  <p className="text-xs text-slate-500">{c.user?.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                <StatMini label="Profile" done={!!c.profile} />
                <StatMini label="Grade" value={c.profile?.grade ? `Class ${c.profile.grade}` : "—"} />
                <StatMini label="Quiz" done={c.has_quiz} />
                <StatMini label="Roadmap" done={c.has_roadmap} />
              </div>
              {c.share_token && (
                <a href={`/share/${c.share_token}`} target="_blank" rel="noreferrer" data-testid={`child-view-${c.user_id}`} className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-blue-600 hover:underline">
                  Open full snapshot <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatMini({ label, done, value }) {
  return (
    <div className="p-2 rounded-lg bg-slate-50 border border-slate-100">
      <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">{label}</p>
      <p className="text-sm text-slate-900 font-bold">{value !== undefined ? value : (done ? "Yes" : "—")}</p>
    </div>
  );
}
